import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Calendar,
  FileSignature,
  ShieldCheck,
  BookOpen,
} from "lucide-react";

const RESPONSABLE = "Daniela Muñoz Jiménez";
const CORREO_PRIVACIDAD = "privacidad@siempresalud.mx";
const VERSION_DOC = "v1.0";
const FECHA_VIGENCIA = "11 de febrero de 2026";

const SECCIONES = [
  { id: "objeto", label: "1. Objeto del acuerdo" },
  { id: "naturaleza", label: "2. Naturaleza del servicio y sus límites" },
  { id: "responsabilidad-usuario", label: "3. Responsabilidad de la persona usuaria" },
  { id: "no-consulta", label: "4. No es consulta médica ni diagnóstico" },
  { id: "repositorio", label: "5. Repositorio de datos de salud" },
  { id: "seguridad", label: "6. Cómo protegemos tus datos" },
  { id: "arco", label: "7. Derechos ARCO y revocación" },
  { id: "deslinde", label: "8. Deslinde de responsabilidad" },
  { id: "aceptacion", label: "9. Aceptación electrónica" },
  { id: "jurisdiccion", label: "10. Jurisdicción y ley aplicable" },
];

export default function AcuerdoDeUso() {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="page-acuerdo-de-uso">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-10">
        <LegalHero
          eyebrow={`Documento legal · ${VERSION_DOC}`}
          title="Acuerdo de Uso y Consentimiento Informado"
          subtitle="Al utilizar Siempre Salud aceptas los términos aquí descritos, incluyendo el consentimiento informado para recibir orientación clínica generada por inteligencia artificial."
          fecha={FECHA_VIGENCIA}
          icon={<FileSignature size={22} />}
        />

        <TocIndex />

        <article className="mt-8 rounded-xl border border-border bg-white p-6 sm:p-8 space-y-8 text-sm text-foreground leading-relaxed">
          <Sec id="objeto" title="1. Objeto del acuerdo">
            <p>
              Este <b>Acuerdo de Uso y Consentimiento Informado</b> (el
              &ldquo;Acuerdo&rdquo;) regula el uso de la plataforma <b>Siempre Salud</b>
              &nbsp;(el &ldquo;Servicio&rdquo;), operada por <b>{RESPONSABLE}</b>
              &nbsp;(el &ldquo;Responsable&rdquo;), y establece el consentimiento
              informado de la persona usuaria para el tratamiento de sus datos
              personales sensibles de salud y la recepción de orientación clínica
              generada por inteligencia artificial.
            </p>
            <p>
              La aceptación electrónica de este Acuerdo tiene el valor de
              <b> consentimiento expreso</b>, conforme al artículo 8 de la Ley
              Federal de Protección de Datos Personales en Posesión de los
              Particulares (LFPDPPP) y a los artículos 1803 y 1811 del Código Civil
              Federal, en relación con la formación de consentimiento por medios
              electrónicos.
            </p>
          </Sec>

          <Sec id="naturaleza" title="2. Naturaleza del servicio y sus límites">
            <p>
              El Servicio consiste en una <b>orientación educativa e informativa</b>
              &nbsp;sobre el <b>uso responsable de medicamentos de venta libre (OTC)</b>
              &nbsp;correspondientes a las <b>Fracciones V y VI</b> del artículo 226 de
              la Ley General de Salud (COFEPRIS), generada por sistemas de inteligencia
              artificial que consultan las <b>Guías de Práctica Clínica (GPC)</b>
              &nbsp;autorizadas por el Sector Salud de los Estados Unidos Mexicanos.
            </p>
            <p>
              La orientación producida por el Servicio <b>no constituye</b>:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Un acto médico ni una consulta médica.</li>
              <li>Un diagnóstico clínico.</li>
              <li>Una prescripción o receta médica.</li>
              <li>Un tratamiento ni sustituto de tratamiento.</li>
              <li>Una segunda opinión médica.</li>
            </ul>
            <p>
              El Servicio <b>nunca recomienda medicamentos que requieran receta médica</b>
              &nbsp;(Fracciones I a IV). Ante síntomas graves, red flags clínicas o
              condiciones que excedan una orientación OTC autolimitada, el Servicio
              deriva a la persona usuaria al Directorio de Especialistas y le
              recomienda acudir a un profesional de la salud.
            </p>
            <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
              <b>En caso de emergencia médica llama de inmediato al 911</b> o acude
              a la unidad de urgencias más cercana. El Servicio <b>no es apto para
              urgencias</b>.
            </p>
          </Sec>

          <Sec id="responsabilidad-usuario" title="3. Responsabilidad de la persona usuaria">
            <p>La persona usuaria declara y acepta expresamente:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Ser <b>mayor de edad</b> y contar con plena capacidad jurídica para
                obligarse en términos del presente Acuerdo.
              </li>
              <li>
                Ser <b>única y exclusiva responsable</b> del uso que haga de la
                plataforma y de las decisiones que tome con base en la orientación
                recibida.
              </li>
              <li>
                Proveer información <b>veraz, completa y actualizada</b> sobre su
                historia clínica, alergias, medicación actual, condiciones especiales
                y síntomas. La orientación se basa exclusivamente en lo declarado
                por la persona usuaria: información incompleta o inexacta puede
                comprometer la seguridad de la orientación.
              </li>
              <li>
                Utilizar el Servicio de <b>buena fe</b>, con fines personales y sin
                intentar vulnerar sus medidas de seguridad, extraer datos de otras
                personas usuarias ni utilizar la orientación con fines diagnósticos
                para terceros sin su consentimiento.
              </li>
              <li>
                Consultar siempre a un <b>profesional de la salud</b> ante cualquier
                duda médica, previo al inicio de un tratamiento o si los síntomas
                persisten, empeoran o presentan características atípicas.
              </li>
            </ul>
          </Sec>

          <Sec id="no-consulta" title="4. Consentimiento informado: no es consulta médica">
            <p>
              <b>Otorgo mi consentimiento informado</b>, libre, expreso e inequívoco
              para recibir la orientación clínica descrita, entendiendo y aceptando
              plenamente que:
            </p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>
                La orientación es <b>generada por inteligencia artificial</b>, no por
                un profesional de la salud humano, y está sujeta a las limitaciones
                inherentes de estos sistemas.
              </li>
              <li>
                La orientación se restringe a medicamentos <b>OTC (Fracciones V y VI
                COFEPRIS)</b>, con base 100% en Guías de Práctica Clínica de México.
              </li>
              <li>
                El Servicio <b>no establece una relación médico-paciente</b> entre la
                persona usuaria y el Responsable, ni entre la persona usuaria y los
                especialistas listados en el Directorio.
              </li>
              <li>
                Los especialistas del Directorio de Siempre Salud son
                <b> contactos directos</b>: cualquier consulta que se dé entre la
                persona usuaria y un especialista es una relación independiente al
                Servicio.
              </li>
            </ol>
          </Sec>

          <Sec id="repositorio" title="5. Repositorio de datos de salud (no es expediente clínico)">
            <p>
              La plataforma <b>no constituye ni almacena un expediente clínico</b> en
              los términos de la NOM-004-SSA3-2012. Lo que la persona usuaria
              carga y captura en el Servicio (historia clínica personal declarada,
              archivos de laboratorio, síntomas, respuestas de IA) integra un
              <b> Repositorio de Datos de Salud</b> aportado <b>voluntariamente</b> por la
              persona usuaria para su uso personal.
            </p>
            <p>
              La persona usuaria puede, en cualquier momento, autorizar a
              especialistas del Directorio a leer su Repositorio de Datos de Salud
              y revocar dicha autorización, sin costo y con efecto inmediato.
            </p>
            <p>
              El personal administrativo autorizado del Responsable puede acceder
              al Repositorio de Datos de Salud <b>únicamente</b> para fines de soporte,
              supervisión y cumplimiento normativo, con registro de auditoría de
              cada acceso, conforme al Aviso de Privacidad Integral.
            </p>
          </Sec>

          <Sec id="seguridad" title="6. Cómo protegemos tu información">
            <p>
              Aplicamos medidas técnicas y organizativas razonables para proteger
              tus datos. En lenguaje claro:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <b>Cifrado a nivel de campo con AES-256-GCM.</b> Cada dato sensible
                (historia clínica, síntomas, respuestas de IA, nombres de archivos
                de laboratorio) se cifra con una clave de 256 bits <b>antes</b> de
                escribirse en la base de datos. Cada operación usa un <b>nonce único</b>,
                de modo que los datos quedan <b>ilegibles</b> incluso ante un acceso
                no autorizado a la base de datos.
              </li>
              <li>
                <b>Transporte seguro (TLS 1.2 o superior).</b> Todo el tráfico entre
                tu navegador y nuestros servidores viaja cifrado.
              </li>
              <li>
                <b>URLs firmadas temporales.</b> Para descargar tus laboratorios o
                documentos, generamos enlaces firmados criptográficamente que
                <b> expiran en 5 minutos</b> y sólo funcionan para la persona usuaria
                autorizada.
              </li>
              <li>
                <b>Contraseñas irreversibles.</b> Almacenamos únicamente el
                <b> hash bcrypt</b> de tu contraseña — jamás la contraseña en texto
                plano.
              </li>
              <li>
                <b>Registros de auditoría inmutables.</b> Cada acceso, edición y
                autorización se registra en una bitácora append-only, incluidos
                los accesos del personal administrativo autorizado.
              </li>
              <li>
                <b>Control de acceso por rol</b> con principio de mínimo privilegio.
              </li>
            </ul>
            <p>
              Ninguna medida de seguridad es infalible. Ante una vulneración
              significativa, el Responsable notificará conforme al artículo 20 de
              la LFPDPPP.
            </p>
          </Sec>

          <Sec id="arco" title="7. Derechos ARCO y revocación">
            <p>
              La persona usuaria tiene los derechos de <b>Acceso, Rectificación,
              Cancelación y Oposición (ARCO)</b> respecto de sus datos personales, así
              como de <b>revocar el consentimiento</b> otorgado en este Acuerdo.
            </p>
            <p className="font-semibold text-[#0F2942]">Cómo ejercerlos, paso a paso:</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>
                Envía un correo a{" "}
                <a className="link" href={`mailto:${CORREO_PRIVACIDAD}`}>
                  {CORREO_PRIVACIDAD}
                </a>{" "}
                con asunto <b>&ldquo;Ejercicio de derechos ARCO&rdquo;</b>.
              </li>
              <li>
                Incluye tu nombre completo, correo con el que creaste la cuenta y
                documento oficial vigente (INE, pasaporte o cédula profesional).
              </li>
              <li>
                Describe el derecho que ejerces (acceso, rectificación, cancelación,
                oposición o revocación de consentimiento) y los datos concretos.
              </li>
              <li>
                El Responsable atenderá tu solicitud en un plazo máximo de
                <b> 20 días hábiles</b> contados a partir de su recepción, y de
                resultar procedente la hará efectiva en los siguientes
                <b> 15 días hábiles</b>.
              </li>
            </ol>
            <p>
              Si no recibes respuesta satisfactoria, puedes acudir al{" "}
              <b>Instituto Nacional de Transparencia, Acceso a la Información y
              Protección de Datos Personales (INAI)</b> en{" "}
              <a
                className="link"
                href="https://home.inai.org.mx/"
                target="_blank"
                rel="noopener noreferrer"
              >
                home.inai.org.mx
              </a>{" "}
              o al teléfono <span className="font-mono">55 5004 2400</span>.
            </p>
            <p>
              La revocación del consentimiento implicará que dejarás de tener
              acceso al Servicio, ya que su prestación depende del tratamiento de
              los datos sensibles descritos.
            </p>
          </Sec>

          <Sec id="deslinde" title="8. Deslinde de responsabilidad">
            <p>
              En la máxima medida permitida por la legislación aplicable, la
              persona usuaria acepta que el uso del Servicio se realiza
              <b> bajo su propio riesgo</b>. El Responsable, sus colaboradores,
              proveedores y filiales <b>no serán responsables</b> por:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Cualquier decisión clínica, terapéutica o de autocuidado que la
                persona usuaria adopte con base en la orientación recibida.
              </li>
              <li>
                Reacciones adversas, alergias, interacciones u otros efectos
                derivados del uso de medicamentos, cuando la persona usuaria haya
                omitido o proporcionado información inexacta al Servicio.
              </li>
              <li>
                La calidad, oportunidad o resultado de los servicios que un
                especialista listado en el Directorio preste a la persona usuaria.
              </li>
              <li>
                Fallas temporales del Servicio, interrupciones, mantenimiento o
                eventos de fuerza mayor.
              </li>
              <li>
                El uso indebido de la cuenta por parte de terceros derivado de una
                falla en el resguardo de las credenciales por la persona usuaria.
              </li>
            </ul>
            <p>
              La persona usuaria <b>indemnizará y mantendrá en paz y a salvo</b> al
              Responsable de cualquier reclamación de terceros derivada del uso
              indebido del Servicio.
            </p>
          </Sec>

          <Sec id="aceptacion" title="9. Aceptación electrónica y firma">
            <p>
              La persona usuaria manifiesta su aceptación de este Acuerdo mediante
              la <b>firma electrónica simple</b> consistente en: (i) marcar la casilla
              &ldquo;He leído y acepto&rdquo;, (ii) escribir su nombre completo tal
              como aparece en su identificación oficial y (iii) presionar el botón
              de firma.
            </p>
            <p>
              Esta manifestación de voluntad, en conjunto con los datos técnicos
              (fecha, hora, dirección IP, agente de usuario) registrados al
              momento de la firma, constituyen <b>consentimiento expreso</b> con pleno
              valor probatorio conforme al artículo 89 del Código de Comercio y a
              los artículos 1803 y 1811 del Código Civil Federal.
            </p>
            <p>
              El registro de aceptación es <b>inmutable</b> (append-only) y podrá ser
              exhibido como prueba en cualquier procedimiento administrativo,
              civil o judicial en el que se cuestione la aceptación por la persona
              usuaria.
            </p>
          </Sec>

          <Sec id="jurisdiccion" title="10. Jurisdicción y ley aplicable">
            <p>
              Este Acuerdo se rige por las leyes vigentes en los Estados Unidos
              Mexicanos. Para la interpretación, cumplimiento y ejecución de este
              Acuerdo, las partes se someten expresamente a la jurisdicción de los
              tribunales competentes de la Ciudad de México, renunciando a
              cualquier otro fuero que pudiera corresponderles por razón de sus
              domicilios presentes o futuros.
            </p>
          </Sec>

          <div className="pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-muted-foreground">
            <div>
              <Calendar size={12} className="inline mr-1" />
              Versión <b>{VERSION_DOC}</b> · vigente desde <b>{FECHA_VIGENCIA}</b>
            </div>
            <div>Responsable: {RESPONSABLE}</div>
          </div>
        </article>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <Link to="/" data-testid="acuerdo-back-btn">
            <Button variant="outline" size="sm">
              <ArrowLeft size={14} /> Volver al inicio
            </Button>
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/aviso-de-privacidad" data-testid="acuerdo-to-privacidad-link">
              <Button variant="ghost" size="sm" className="text-[#2563EB]">
                <ShieldCheck size={14} /> Aviso de Privacidad
              </Button>
            </Link>
            <Link to="/terminos-y-condiciones" data-testid="acuerdo-to-terminos-link">
              <Button variant="ghost" size="sm" className="text-[#2563EB]">
                <BookOpen size={14} /> Términos y Condiciones
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function LegalHero({ eyebrow, title, subtitle, fecha, icon }) {
  return (
    <section className="rounded-xl border border-border bg-white p-6 sm:p-8">
      <div className="flex items-start justify-between gap-3">
        <div className="max-w-2xl">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
            {eyebrow}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#0F2942] tracking-tight mt-1">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{subtitle}</p>
          )}
          <div className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <Calendar size={12} /> Vigente desde: <b>{fecha}</b>
          </div>
        </div>
        <div className="w-10 h-10 rounded-lg bg-[#0F2942]/5 text-[#0F2942] grid place-items-center shrink-0">
          {icon}
        </div>
      </div>
    </section>
  );
}

function TocIndex() {
  return (
    <nav
      className="mt-6 rounded-xl border border-border bg-white p-5"
      aria-label="Índice del documento"
      data-testid="acuerdo-toc"
    >
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
        Contenido
      </div>
      <ol className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        {SECCIONES.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className="text-[#0F2942] hover:text-[#2563EB] hover:underline decoration-dotted underline-offset-4"
            >
              {s.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Sec({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`acuerdo-sec-${id}`}>
      <h2 className="font-display text-lg sm:text-xl font-semibold text-[#0F2942] tracking-tight">
        {title}
      </h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  );
}
